import { useState, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { MICROPHONE_AUDIO_PROFILE } from '@/shared/config';
import { mediaStreamTracker } from '@/shared/utils/mediaStreamTracker';

import { transcriptorRecordingMessages } from '../config/i18n';
import { MicrophoneDevice } from '../config/types';

export const useMicrophonePermissions = () => {
  const { formatMessage } = useIntl();
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [isPermanentlyDenied, setIsPermanentlyDenied] = useState(false);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [devices, setDevices] = useState<MicrophoneDevice[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);

  const checkMicrophonePermission = useCallback(async () => {
    try {
      setIsLoading(true);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const streamId = mediaStreamTracker.addStream(stream, 'checkPermission');

      // Get available audio input devices
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const audioDevices = allDevices
        .filter((device) => device.kind === 'audioinput')
        .map((device) => ({
          deviceId: device.deviceId,
          label:
            device.label ||
            formatMessage(
              transcriptorRecordingMessages.microphoneFallbackLabel,
              {
                deviceId: device.deviceId,
              },
            ),
          groupId: device.groupId,
        }));

      setDevices(audioDevices);

      if (!selectedDevice && audioDevices.length > 0) {
        setSelectedDevice(audioDevices[0].deviceId);
      }

      stream.getTracks().forEach((track) => track.stop());
      mediaStreamTracker.removeStream(streamId);

      setHasPermission(true);
      setIsPermanentlyDenied(false);
      setPermissionError(null);
    } catch (error) {
      setHasPermission(false);

      try {
        const permission = await navigator.permissions.query({
          name: 'microphone' as PermissionName,
        });
        if (permission.state === 'denied') {
          setIsPermanentlyDenied(true);
        }
      } catch (e) {
        // ignore
      }

      setPermissionError(
        error instanceof Error
          ? error.message
          : formatMessage(transcriptorRecordingMessages.microphoneAccessDenied),
      );
    } finally {
      setIsLoading(false);
    }
  }, [selectedDevice, formatMessage]);

  const startAudioLevelMonitoring = useCallback(async (deviceId: string) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { ...MICROPHONE_AUDIO_PROFILE, deviceId: { exact: deviceId } },
      });
      const streamId = mediaStreamTracker.addStream(
        stream,
        'audioLevelMonitoring',
      );

      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);

      analyser.smoothingTimeConstant = 0.8;
      analyser.fftSize = 1024;

      microphone.connect(analyser);

      const array = new Uint8Array(analyser.frequencyBinCount);
      let animationId: number;
      let lastUpdateTime = 0;
      const UPDATE_INTERVAL = 100; // Update every 100ms instead of every frame

      const updateAudioLevel = () => {
        const currentTime = performance.now();

        // Only update if enough time has passed
        if (currentTime - lastUpdateTime >= UPDATE_INTERVAL) {
          analyser.getByteFrequencyData(array);
          const arraySum = array.reduce((a, value) => a + value, 0);
          const average = arraySum / array.length;
          setAudioLevel(Math.round((average / 255) * 100));
          lastUpdateTime = currentTime;
        }

        animationId = requestAnimationFrame(updateAudioLevel);
      };

      updateAudioLevel();

      return () => {
        cancelAnimationFrame(animationId);

        stream.getTracks().forEach((track) => track.stop());

        analyser.disconnect();
        microphone.disconnect();

        // Only close if not already closed
        if (audioContext.state !== 'closed') {
          audioContext.close();
        }

        mediaStreamTracker.removeStream(streamId);
      };
    } catch (error) {
      return undefined;
    }
  }, []);

  // Check initial permission state when hook mounts
  useEffect(() => {
    const checkInitialPermission = async () => {
      try {
        // Check if permission is already granted
        const permission = await navigator.permissions.query({
          name: 'microphone' as PermissionName,
        });
        if (permission.state === 'granted') {
          await checkMicrophonePermission();
        } else if (permission.state === 'denied') {
          setHasPermission(false);
          setIsPermanentlyDenied(true);
          setPermissionError(
            formatMessage(transcriptorRecordingMessages.microphoneAccessDenied),
          );
        } else {
          // Permission state is 'prompt' - user hasn't been asked yet
          setHasPermission(false);
        }
      } catch (error) {
        // Fallback for browsers that don't support permissions API
        setHasPermission(false);
      }
    };

    checkInitialPermission();
  }, [checkMicrophonePermission, formatMessage]);

  useEffect(() => {
    if (!hasPermission) {
      return;
    }

    const handleDeviceChange = () => {
      checkMicrophonePermission();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    // Polling fallback: devicechange event is unreliable on some mobile devices.
    // Poll every 2 seconds and compare device list to detect changes.
    let previousDeviceIds = devices.map((d) => d.deviceId).join(',');

    const pollInterval = window.setInterval(async () => {
      try {
        const allDevices = await navigator.mediaDevices.enumerateDevices();
        const audioDeviceIds = allDevices
          .filter((d) => d.kind === 'audioinput')
          .map((d) => d.deviceId)
          .join(',');

        if (audioDeviceIds !== previousDeviceIds) {
          previousDeviceIds = audioDeviceIds;
          checkMicrophonePermission();
        }
      } catch {
        // Ignore enumeration errors during polling
      }
    }, 2000);

    return () => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        handleDeviceChange,
      );
      window.clearInterval(pollInterval);
    };
  }, [checkMicrophonePermission, hasPermission]);

  return {
    hasPermission,
    isPermanentlyDenied,
    permissionError,
    devices,
    selectedDevice,
    setSelectedDevice,
    isLoading,
    audioLevel,
    startAudioLevelMonitoring,
    checkMicrophonePermission,
  };
};
